import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["productRow", "selectionInput", "quantityWrapper", "totalDisplay", "totalValue", "validationInput", "errorMessage"]
  
  static values = {
    selectionMode: { type: String, default: 'single' },
    isEarlyPrice: { type: Boolean, default: false },
    required: { type: Boolean, default: false }
  }

  connect() {
    this.calculateTotal()
    this.updateValidation()
    
    // Add form submit listener for validation
    this.form = this.element.closest('form')
    if (this.form && this.requiredValue) {
      this.boundValidate = this.validateOnSubmit.bind(this)
      this.form.addEventListener('submit', this.boundValidate)
    }
  }

  disconnect() {
    if (this.form && this.boundValidate) {
      this.form.removeEventListener('submit', this.boundValidate)
    }
  }

  validateOnSubmit(event) {
    const hasSelection = this.productRowTargets.some(row => row.classList.contains('selected'))
    
    if (!hasSelection) {
      event.preventDefault()
      this.showError()
      this.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return false
    }
    
    this.hideError()
    return true
  }

  showError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'block'
    }
    this.element.classList.add('has-error')
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'none'
    }
    this.element.classList.remove('has-error')
  }

  onSelectionChange(event) {
    const input = event.target
    const row = input.closest('.product-row')
    const isChecked = input.checked
    
    if (this.selectionModeValue === 'single') {
      // For radio buttons, unselect all other rows
      this.productRowTargets.forEach(r => {
        r.classList.remove('selected')
        const qtyWrapper = r.querySelector('[data-payment-product-selection-target="quantityWrapper"]')
        if (qtyWrapper) {
          qtyWrapper.style.visibility = 'hidden'
        }
      })
    }
    
    if (isChecked) {
      row.classList.add('selected')
      const qtyWrapper = row.querySelector('[data-payment-product-selection-target="quantityWrapper"]')
      if (qtyWrapper) {
        qtyWrapper.style.visibility = 'visible'
      }
    } else {
      row.classList.remove('selected')
      const qtyWrapper = row.querySelector('[data-payment-product-selection-target="quantityWrapper"]')
      if (qtyWrapper) {
        qtyWrapper.style.visibility = 'hidden'
      }
    }
    
    this.calculateTotal()
    this.updateValidation()
    this.hideError()
  }

  onQuantityChange(event) {
    this.calculateTotal()
  }

  updateValidation() {
    if (!this.hasValidationInputTarget) return
    
    const hasSelection = this.productRowTargets.some(row => row.classList.contains('selected'))
    this.validationInputTarget.value = hasSelection ? '1' : ''
  }

  calculateTotal() {
    let total = 0
    
    this.productRowTargets.forEach(row => {
      if (row.classList.contains('selected')) {
        const earlyPrice = parseFloat(row.dataset.earlyPrice) || 0
        const regularPrice = parseFloat(row.dataset.regularPrice) || 0
        const price = this.isEarlyPriceValue ? earlyPrice : regularPrice
        
        const quantityInput = row.querySelector('[data-quantity-selector-target="input"]')
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1
        
        total += price * quantity
      }
    })
    
    if (this.hasTotalValueTarget) {
      this.totalValueTarget.textContent = this.formatPrice(total)
    }
  }

  formatPrice(amount) {
    if (isNaN(amount) || amount === null || amount === undefined || amount === '') amount = 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}
